import {SIDEBAR_ROUTES} from '~/src/routes'

/**
 * Navigation Menu Items
 *
 * NOTE: URLs no longer contain role prefixes (e.g., /platform-admin/, /agency-admin/)
 * The layout variant is determined from the user's role in AppRoutes, not from the URL
 *
 * moduleKey: Maps to the permission module for canView checks
 * If undefined, the menu item is always visible
 */
export interface INavMenuItem {
  id: number
  link?: string
  text: string
  subMenuItems?: INavMenuItem[]
  activePattern?: string // Optional regex pattern to detect if this menu item should be active
  moduleKey?: string // Maps to permission module key for visibility checks
  /** Shown only to platform administrators (backendRolePermissions.isPlatform), whatever moduleKey grants */
  platformAdminOnly?: boolean
  /**
   * With moduleKey, the item shows only when the user passes the SAME check the route guard
   * makes for a route keyed on (moduleKey, submoduleKey): hasModulePermission, read the way
   * AppRoutes' RouteRenderer reads it. So the link is offered exactly when it leads somewhere
   * other than /unauthorized.
   */
  submoduleKey?: string
  /** Shown only while this feature flag is on, whatever the permissions say. */
  featureFlag?: string
}

/**
 * The centralized Requests page: Time Off requests across the user's scope (Platform Admin: every
 * agency; MSP / Agency: the switcher's agency and its branches). Same gate as the 'requests' route.
 */
const REQUESTS_ITEM: Omit<INavMenuItem, 'id'> = {
  link: SIDEBAR_ROUTES.REQUESTS,
  text: 'Requests',
  moduleKey: 'requests',
  // TODO(feature-flag): agencyTimeOffRequests — remove when request review is in production
  featureFlag: 'agencyTimeOffRequests',
}

/**
 * STFFLG-4163: the signer's own queue, for agency-side users who hold Settings > eSign > Signing.
 * Same gate as the 'my-signatures' route, so the item is offered exactly to those the route lets
 * in. It was reachable only from the notification bell (which drops a request once read), the
 * post-signing Done button, or by typing the URL; SHR-FE has had a 'My Signatures' item all along.
 * MSP-category roles cannot hold Signing, so the gate hides it from them: an MSP EOR admin reaches
 * their document through the email's link to /signing/:id, which is ungated.
 */
const MY_SIGNATURES_ITEM: Omit<INavMenuItem, 'id'> = {
  link: '/my-signatures',
  text: 'My Signatures',
  moduleKey: 'settings',
  submoduleKey: 'esign_signing',
}

/**
 * "Manage Profile" on the agency-side layouts (agency, main-agency, msp) links to /manage-agency,
 * which redirects to /manage-agencies/{loggedInAgencyId}/profile — so highlight on that page.
 */
const MANAGE_OWN_AGENCY_PATTERN = '/manage-agencies/[^/]+'

export const platformAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Manage Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    link: SIDEBAR_ROUTES.MANAGE_AGENCIES,
    text: 'Manage Agencies',
    moduleKey: 'manage_agencies',
  },
  {
    id: 4,
    link: '/users-roles',
    text: 'Users',
    moduleKey: 'users_roles',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.PAYROLL,
    text: 'Payroll',
    moduleKey: 'payroll',
  },
  {
    // KRAF-763: bank reconciliation and collections, on the payroll permission; platform admins
    // only, like the reconciliation API (PlatformAdminGuard).
    id: 9,
    link: SIDEBAR_ROUTES.FINANCE,
    text: 'Finance',
    moduleKey: 'payroll',
    platformAdminOnly: true,
  },
  {
    id: 6,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {id: 8, ...REQUESTS_ITEM},
  {
    id: 7,
    link: SIDEBAR_ROUTES.REPORTS,
    text: 'Reports',
    moduleKey: 'reports',
  },
  // Note: Global dropdown is handled by GlobalDropdown.tsx component, not MenuItems
  // {
  //   id: 8,
  //   link: SIDEBAR_ROUTES.IMPORT,
  //   text: 'Import Candidates',
  // },
]

export const agencyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    // Pending/Current/Completed now live as in-page pill tabs (ManageOrdersTabs),
    // not a nav dropdown. Single link to Current Orders; activePattern keeps the
    // nav item highlighted across all /manage-orders sub-routes.
    link: SIDEBAR_ROUTES.SERVICE_ORDERS,
    activePattern: '/manage-orders',
    moduleKey: 'manage_orders',
    // STFFLG-4269: the route's own gate (agency-service-orders), so the link never bounces
    submoduleKey: 'service_orders',
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_AGENCY,
    text: 'Manage Profile',
    activePattern: MANAGE_OWN_AGENCY_PATTERN,
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {id: 7, ...REQUESTS_ITEM},
  {
    id: 6,
    link: SIDEBAR_ROUTES.REPORTS,
    text: 'Reports',
    moduleKey: 'reports',
  },
  {
    id: 8,
    link: '/users-roles',
    text: 'Users & Roles',
    // Uses agency_users_roles for agency admin (submodule of manage_profile)
    // Route permission check has aliases to handle this
    moduleKey: 'users_roles',
  },
  {id: 9, ...MY_SIGNATURES_ITEM},
]

/**
 * Company Admin Navigation
 *
 * API modulePermissions for companyadmin:
 * - dashboard: Dashboard
 * - create_orders: Create Orders (button, not nav item)
 * - company_manage_profile: Manage Profile (contains company_positions, company_users_roles, etc.)
 * - company_manage_orders: Manage Orders (service_orders, job_orders)
 * - other_settings: Other Settings
 * - company_invoices: Invoices
 * - review_approve: Review and Approve
 * - company_timesheets: Timesheets
 * - company_documents: Documents
 *
 * Note: "Positions" nav uses company_positions (submodule of company_manage_profile)
 * The same data is shown in both nav Positions and Manage Profile -> Positions tab
 */
export const companyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.POSITIONS,
    text: 'Positions',
    // Uses company_positions which is a submodule of company_manage_profile
    moduleKey: 'company_positions',
  },
  {
    id: 3,
    text: 'Manage Orders',
    // Service Orders / Completed Orders now live as in-page pill tabs (ManageOrdersTabs),
    // not a nav dropdown. Single link to Service Orders; activePattern keeps the nav item
    // highlighted across all /manage-orders sub-routes. Matches the MSP/agency nav.
    link: SIDEBAR_ROUTES.SERVICE_ORDERS,
    activePattern: '/manage-orders',
    moduleKey: 'company_manage_orders',
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_COMPANY,
    text: 'Manage Profile',
    activePattern: '/manage-companies/[^/]+', // Matches /manage-companies/{companyId}/*
    moduleKey: 'company_manage_profile',
  },
  {
    id: 5,
    link: '/invoices',
    text: 'Invoices',
    moduleKey: 'company_invoices',
  },
  {
    id: 6,
    link: '/timesheets',
    text: 'Timesheets',
    moduleKey: 'company_timesheets',
  },
  {
    id: 7,
    link: '/users-roles',
    text: 'Users & Roles',
    // Uses company_users_roles which is a submodule of company_manage_profile
    moduleKey: 'company_users_roles',
  },
]

export const mspAgencyList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    // Pending/Current/Completed now live as in-page pill tabs (ManageOrdersTabs),
    // not a nav dropdown. Single link to Current Orders; activePattern keeps the
    // nav item highlighted across all /manage-orders sub-routes.
    link: SIDEBAR_ROUTES.SERVICE_ORDERS,
    activePattern: '/manage-orders',
    moduleKey: 'manage_orders',
    // STFFLG-4269: the route's own gate (agency-service-orders), so the link never bounces
    submoduleKey: 'service_orders',
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_AGENCY,
    text: 'Manage Profile',
    activePattern: MANAGE_OWN_AGENCY_PATTERN,
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {id: 10, ...REQUESTS_ITEM},
  {
    id: 6,
    link: SIDEBAR_ROUTES.REPORTS,
    text: 'Reports',
    moduleKey: 'reports',
  },
  {
    id: 7,
    link: '/users-roles',
    text: 'Users & Roles',
    moduleKey: 'users_roles',
  },
  {id: 8, ...MY_SIGNATURES_ITEM},
]

/**
 * General Admin Navigation
 *
 * API modulePermissions for general_admin:
 * - global: Global (with roles_permissions submodule) - accessed via GlobalDropdown
 * - manage_companies: Manage Companies (12 submodules)
 * - manage_agencies: Manage Agencies (13 submodules)
 * - directory: Directory (view only)
 *
 * Note: GA is a cross-agency admin role for managing companies/agencies without operational access
 * The "Global" dropdown is added separately in NavMenuBar, not as a nav item here
 */
export const generalAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Manage Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_AGENCIES,
    text: 'Manage Agencies',
    moduleKey: 'manage_agencies',
  },
  {
    id: 3,
    link: '/directory',
    text: 'Directory',
    moduleKey: 'general_directory',
  },
]

export const mainAgencyAdminList: INavMenuItem[] = [
  {
    id: 1,
    link: SIDEBAR_ROUTES.DASHBOARD,
    text: 'Dashboard',
    moduleKey: 'dashboard',
  },
  {
    id: 2,
    link: SIDEBAR_ROUTES.MANAGE_COMPANIES,
    text: 'Companies',
    moduleKey: 'manage_companies',
  },
  {
    id: 3,
    text: 'Manage Orders',
    // Pending/Current/Completed now live as in-page pill tabs (ManageOrdersTabs),
    // not a nav dropdown. Single link to Current Orders; activePattern keeps the
    // nav item highlighted across all /manage-orders sub-routes.
    link: SIDEBAR_ROUTES.SERVICE_ORDERS,
    activePattern: '/manage-orders',
    moduleKey: 'manage_orders',
    // STFFLG-4269: the route's own gate (agency-service-orders), so the link never bounces
    submoduleKey: 'service_orders',
  },
  {
    id: 4,
    link: SIDEBAR_ROUTES.MANAGE_AGENCY,
    text: 'Manage Profile',
    activePattern: MANAGE_OWN_AGENCY_PATTERN,
    moduleKey: 'manage_profile',
  },
  {
    id: 5,
    link: SIDEBAR_ROUTES.CANDIDATES,
    text: 'Candidates',
    moduleKey: 'candidates',
  },
  {id: 8, ...REQUESTS_ITEM},
  {
    id: 6,
    link: SIDEBAR_ROUTES.REPORTS,
    text: 'Reports',
    moduleKey: 'reports',
  },
  {
    id: 7,
    link: '/users-roles',
    text: 'Users & Roles',
    moduleKey: 'users_roles',
  },
]

export const candidateList: INavMenuItem[] = [
  {id: 1, link: '/dashboard', text: 'Dashboard', moduleKey: 'candidate_dashboard'},
  {id: 2, link: '/my-profile', text: 'My Info', moduleKey: 'candidate_profile'},
  {id: 3, link: '/paychecks', text: 'Paychecks', moduleKey: 'paychecks'},
  {id: 4, link: '/documents', text: 'Documents', moduleKey: 'candidate_documents'},
]
